import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useInvite } from "@/hooks/useInvite";
import { CandidateFlow } from "@/shell/CandidateFlow";
import { Button, Spinner } from "@/components/primitives";
import { InviteError } from "./InviteError";
import { candidateApi } from "@/lib/candidateApi";

// C7 — Notification pre-prompt. Shown before the browser permission dialog
// to explain what we'll send and why. Significantly improves accept rates
// vs showing the cold OS dialog directly.
// B1.4: also captures interview date for 24h/2h reminder scheduling.

export function NotificationPrompt() {
  const { token, sessionId } = useParams<{ token: string; sessionId: string }>();
  const navigate = useNavigate();
  const { data, error, loading } = useInvite(token);
  const [requesting, setRequesting] = useState(false);
  const [interviewDate, setInterviewDate] = useState("");

  const proceed = useCallback(() => {
    navigate(`/invite/${token}/prep/${sessionId}`);
  }, [navigate, token, sessionId]);

  async function allowReminders() {
    setRequesting(true);
    try {
      if ("Notification" in window && Notification.permission === "default") {
        await Notification.requestPermission();
      }
      await candidateApi.notificationOptin(token!, interviewDate || null, Notification.permission === "granted");
    } catch {
      // proceed regardless of errors
    }
    setRequesting(false);
    proceed();
  }

  if (loading) {
    return (
      <CandidateFlow>
        <div className="flex justify-center py-10"><Spinner size={28} /></div>
      </CandidateFlow>
    );
  }
  if (error || !data) return <InviteError message={error ?? "Invite not found."} />;

  return (
    <CandidateFlow
      agencyName={data.agencyName}
      logoUrl={data.branding?.logoUrlLight || undefined}
      logoFilter={data.branding?.logoFilter}
    >
      <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-md bg-accent-50 text-accent">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M13.73 21a2 2 0 01-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      <h1 className="mb-2 text-xl font-semibold tracking-[-0.3px] text-ink">
        Stay on track for your interview
      </h1>

      <p className="mb-5 text-sm leading-relaxed text-slate-500">
        We'll remind you before your interview to complete a quick final practice session.
      </p>

      <div className="mb-4">
        <label className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Interview date &amp; time
        </label>
        <input
          type="datetime-local"
          value={interviewDate}
          onChange={(e) => setInterviewDate(e.target.value)}
          className="w-full rounded-lg border border-hair bg-cream px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          min={new Date().toISOString().slice(0, 16)}
        />
      </div>

      <div className="mb-5 rounded-lg border border-hair bg-cream p-4">
        <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          What you'll receive
        </p>
        <div className="space-y-2.5">
          {[
            "A reminder 24 hours before your interview",
            "A reminder 2 hours before — last chance for a quick practice",
            "Nothing else — no marketing, no spam",
          ].map((item) => (
            <div key={item} className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-good-50 text-good">
                <svg width="9" height="9" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M5 10.5l3.5 3.5L15 6.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="text-sm text-ink-2">{item}</span>
            </div>
          ))}
        </div>
      </div>

      <Button
        variant="accent"
        className="mb-3 w-full"
        onClick={allowReminders}
        disabled={requesting}
      >
        {requesting ? <Spinner size={16} /> : interviewDate ? "Yes, remind me" : "Continue without reminders"}
      </Button>

      <button type="button" onClick={proceed} className="w-full text-center text-sm text-slate-400 hover:text-slate-600">
        Skip for now
      </button>
    </CandidateFlow>
  );
}
